import Button from 'components/ui/button';
import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { getNews, getNewsLog, updateNewLog } from 'services/news.service';
import { updateUserProfile } from 'services/profile.service';

import './styles.scss';
import { UserInfo } from 'types/user';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  send: () => void;
  player: UserInfo | null | undefined;
}

export default function NewFeatures(data: Props) {
  const { send, player } = data;
  const [news, setNews] = useState<any>();
  const [didInit, setDidInit] = useState(false);
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();

  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const handleCloseTutorialRef = useRef<() => void>(() => {});

  const loadNews = useCallback(async () => {
    if (loading || !user || !player) return;

    setLoading(true);
    setDidInit(true);

    try {
      const log = await getNewsLog();
      const newsData = await getNews(player.cad_missao, log?.time);

      if (newsData) {
        setNews(newsData);
      } else {
        send();
      }
    } finally {
      setLoading(false);
    }
  }, [loading, player, send, user]);

  const handleCloseTutorial = useCallback(async () => {
    if (!user) return;
    setNews(undefined);

    await updateNewLog(user?._id, news?.created);

    loadNews();
  }, [loadNews, news?.created, user]);

  handleCloseTutorialRef.current = handleCloseTutorial;

  useEffect(() => {
    if (didInit) return;
    loadNews();
  }, [didInit, loadNews]);

  useEffect(() => {
    window.addEventListener('close-news', handleCloseTutorial);
    return () => {
      window.removeEventListener('close-news', handleCloseTutorial);
    };
  }, [handleCloseTutorial]);

  // Focus trap
  useEffect(() => {
    if (!news) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const rafId = requestAnimationFrame(() => {
      const container = dialogRef.current;
      if (!container) return;
      const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
      first.focus();
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleCloseTutorialRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const container = dialogRef.current;
      if (!container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) { e.preventDefault(); return; }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (!container.contains(document.activeElement) || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!container.contains(document.activeElement) || document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function handleFocusIn(e: FocusEvent) {
      const container = dialogRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) {
        const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [news]);

  if (!news) return null;

  return (
    <div className="z-50 modal-news fixed bg-black bg-opacity-40 top-0 left-0 w-screen h-screen p-6 pt-[10vh] flex justify-center overflow-y-auto">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Novidades"
        tabIndex={-1}
        className="relative border-2 border-solid border-electric-blue bg-oxford-blue rounded-lg p-10 max-w-[600px] h-fit"
      >
        <Button
          color="primary"
          className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center z-50"
          onClick={() => handleCloseTutorial()}
          aria-label="Fechar novidades"
        >
          <IoIosClose color="white" size={30} />
        </Button>

        {news?.text && <div dangerouslySetInnerHTML={{ __html: news.text }}></div>}
      </div>
    </div>
  );
}
